/**
 * Por que no cuadra una factura de proveedor contra su XML.
 *
 * Las pantallas de captura ya comparan el total del CFDI contra el capturado y bloquean el
 * guardado si difieren, pero solo dicen QUE no cuadra. Este modulo dice POR QUE: descompone la
 * diferencia (subtotal / impuestos / retenciones), la reparte entre causas concretas y, cuando
 * puede, senala el renglon culpable.
 *
 * Es un modulo PURO: sin React, sin axios, sin modelos. Se puede probar con un `node script.js`.
 *
 * El insumo de todo esto es el arbol que devuelve `Components/xmlToJson`: nodos con `name`
 * prefijado ("cfdi:Impuestos") y atributos PELONES dentro de `.attributes`. El helper
 * `atributo()` de erp_backend/Utils/cfdiComplemento.utils.js NO cubre ese formato; no sirve aqui.
 */

import round2Decimals from "../Components/round2Decimals";
import round6Decimals from "../Components/round6Decimals";
import decodeHTMLEntities from "../Components/decodeHtmlEntities";
import { leerImpuestosCfdi, nombreCodigoSat, formatearTasa } from "./impuestosXmlCompra";

// Lo que las pantallas consideran "cuadrado": igualdad exacta despues de redondear a 2.
const TOLERANCIA_CENTAVO = 0.005;
// Arriba de esto un residuo ya no se puede achacar al redondeo del emisor.
const TOPE_REDONDEO = 1;

const numero = (value) => {
  const convertido = Number(value);
  return Number.isFinite(convertido) ? convertido : 0;
};

const atributos = (nodo) => nodo?.attributes || {};

// Un lado puede venir con las entidades HTML ya resueltas y el otro no (buscarRelacion decodifica,
// procesarXml devuelve el concepto tal como se le mando), asi que se normalizan los dos.
const texto = (value) => decodeHTMLEntities(String(value ?? "")).trim();

/** Dinero para los textos del diagnostico. dineroDisabledMask vive en Components y trae JSX alrededor. */
const moneda = (value) => {
  const numeroValor = round2Decimals(numero(value));
  const signo = numeroValor < 0 ? "-" : "";
  return (
    signo +
    "$" +
    Math.abs(numeroValor)
      .toFixed(2)
      .replace(/\d{1,3}(?=(\d{3})+(?!\d))/g, "$&,")
  );
};

const hijo = (nodo, nombre) =>
  nodo?.children?.find((valueXml) => valueXml.name === nombre);

const hijos = (nodo, nombre) => hijo(nodo, nombre)?.children || [];

const codigoSatANumero = (codigo) => Number(String(codigo ?? "").trim());

/**
 * La tasa lista para comparar, siempre en porcentaje.
 *
 * El CFDI la declara en fraccion (0.16), pero hay emisores que la escriben ya en porcentaje.
 * Solo se reinterpreta cuando pasa de 1, porque una tasa en fraccion nunca lo hace: si no, un
 * 0.12 legitimo se leeria como 0.12%. Misma regla que `tasasPosibles` de impuestosXmlCompra.
 */
const tasaEnPorcentaje = (tasaOCuota, tipoFactor) => {
  const tasa = numero(tasaOCuota);
  if (tipoFactor === "Exento") return 0;
  // Una cuota son pesos por unidad, no un porcentaje: se deja tal cual.
  if (tipoFactor === "Cuota") return tasa;
  if (tasa > 1) return tasa;
  return round6Decimals(tasa * 100);
};

const claveFiscal = (codigoSat, tipoFactor, tasaPorcentaje) =>
  `${codigoSatANumero(codigoSat)}|${tipoFactor || "Tasa"}|${formatearTasa(tasaPorcentaje)}`;

const etiquetaFiscal = (codigoSat, tipoFactor, tasaPorcentaje) => {
  const nombre = nombreCodigoSat(codigoSat);
  if (tipoFactor === "Exento") return `${nombre} exento`;
  if (tipoFactor === "Cuota") return `${nombre} cuota de ${formatearTasa(tasaPorcentaje)}`;
  return `${nombre} ${formatearTasa(tasaPorcentaje)}%`;
};

//  Lectura del CFDI

const leerConcepto = (nodoConcepto, indice) => {
  const attr = atributos(nodoConcepto);
  const nodoImpuestos = hijo(nodoConcepto, "cfdi:Impuestos");

  const leerNodos = (nombre) =>
    hijos(nodoImpuestos, nombre).map((valueNodo) => {
      const attrNodo = atributos(valueNodo);
      const tipoFactor = attrNodo.TipoFactor || "Tasa";
      return {
        codigoSat: attrNodo.Impuesto,
        tipoFactor,
        tasaOCuota: numero(attrNodo.TasaOCuota),
        tasaPorcentaje: tasaEnPorcentaje(attrNodo.TasaOCuota, tipoFactor),
        base: numero(attrNodo.Base),
        importe: numero(attrNodo.Importe),
      };
    });

  const importe = numero(attr.Importe);
  const descuento = numero(attr.Descuento);

  return {
    indice,
    codigo: attr.NoIdentificacion,
    descripcion: attr.Descripcion,
    claveProdServ: attr.ClaveProdServ,
    claveUnidad: attr.ClaveUnidad,
    cantidad: numero(attr.Cantidad),
    valorUnitario: numero(attr.ValorUnitario),
    importe,
    descuento,
    // Lo que de verdad se compara contra el subtotal del renglon capturado.
    importeNeto: round6Decimals(importe - descuento),
    traslados: leerNodos("cfdi:Traslados"),
    retenciones: leerNodos("cfdi:Retenciones"),
  };
};

/**
 * Complemento `implocal` (ISH y demas impuestos estatales).
 *
 * El sistema NO lo lee en ningun flujo de compras, asi que cuando existe es diferencia pura.
 * La forma correcta es un nodo raiz `implocal:ImpuestosLocales` con hijos
 * `implocal:TrasladosLocales` / `implocal:RetencionesLocales`, pero se clasifica por el atributo
 * y no por el nombre del nodo, para aguantar tambien la forma que lee Pdf/facturaXmlPdf.js
 * (donde los busca colgados directamente del complemento).
 */
const leerImpuestosLocales = (nodoComplemento) => {
  const nodoRaiz = hijo(nodoComplemento, "implocal:ImpuestosLocales");

  const traslados = [];
  const retenciones = [];

  const clasificar = (valueNodo) => {
    const attr = atributos(valueNodo);
    if (attr.ImpLocTrasladado !== undefined) {
      traslados.push({
        nombre: attr.ImpLocTrasladado,
        tasa: numero(attr.TasadeTraslado),
        importe: numero(attr.Importe),
      });
    } else if (attr.ImpLocRetenido !== undefined) {
      retenciones.push({
        nombre: attr.ImpLocRetenido,
        tasa: numero(attr.TasadeRetencion),
        importe: numero(attr.Importe),
      });
    }
  };

  for (const valueNodo of nodoRaiz?.children || []) clasificar(valueNodo);
  for (const valueNodo of nodoComplemento?.children || []) {
    if (valueNodo.name !== "implocal:ImpuestosLocales") clasificar(valueNodo);
  }

  const attrRaiz = atributos(nodoRaiz);
  const sumar = (lista) =>
    round2Decimals(lista.reduce((acumulado, value) => acumulado + value.importe, 0));

  return {
    traslados,
    retenciones,
    totalTraslados: nodoRaiz ? numero(attrRaiz.TotaldeTraslados) || sumar(traslados) : 0,
    totalRetenciones: nodoRaiz ? numero(attrRaiz.TotaldeRetenciones) || sumar(retenciones) : 0,
  };
};

/**
 * Guarda del CFDI todo lo que la captura lee y tira: hoy las pantallas solo conservan
 * `{ SubTotal, Total }`, y sin el desglose no hay diagnostico posible.
 *
 * @param {Object} arbolXml raiz parseada por Components/xmlToJson
 * @param {Object} [opciones]
 * @param {Array} [opciones.insumosDelXml] lista `{ descripcion, clave, insumoId }` con el insumo al
 *   que se resolvio cada concepto. Solo hace falta en las pantallas de orden de compra: ahi los
 *   renglones vienen de la OC y no conservan la identidad del concepto, asi que el unico puente
 *   entre los dos lados es el insumoId.
 * @returns {Object|null}
 */
export function leerCfdiParaDiagnostico(arbolXml, { insumosDelXml } = {}) {
  if (!arbolXml) return null;

  const comprobante = atributos(arbolXml);
  const nodoImpuestos = hijo(arbolXml, "cfdi:Impuestos");
  const nodoComplemento = hijo(arbolXml, "cfdi:Complemento");
  const attrImpuestos = atributos(nodoImpuestos);

  // traslados / retenciones / avisos ya los sabe leer impuestosXmlCompra: los traslados del nodo
  // global y las retenciones agregadas por concepto (el nodo global no trae TasaOCuota).
  const { traslados, retenciones, avisos } = leerImpuestosCfdi(arbolXml);

  const conceptos = (hijo(arbolXml, "cfdi:Conceptos")?.children || []).map(leerConcepto);

  if (Array.isArray(insumosDelXml)) {
    const disponibles = insumosDelXml.filter((value) => value && value.insumoId);
    const usados = new Set();
    for (const concepto of conceptos) {
      const indice = disponibles.findIndex(
        (value, index) =>
          !usados.has(index) &&
          texto(value.descripcion) === texto(concepto.descripcion) &&
          texto(value.clave) === texto(concepto.codigo)
      );
      if (indice !== -1) {
        usados.add(indice);
        concepto.insumoId = disponibles[indice].insumoId;
      }
    }
  }

  const subTotal = numero(comprobante.SubTotal);
  const descuento = numero(comprobante.Descuento);

  const conTasa = (lista) =>
    lista.map((value) => ({
      ...value,
      tasaPorcentaje: tasaEnPorcentaje(value.tasaOCuota, value.tipoFactor),
    }));

  return {
    subTotal,
    descuento,
    subTotalNeto: round6Decimals(subTotal - descuento),
    total: numero(comprobante.Total),
    moneda: comprobante.Moneda || "",
    tipoCambio: numero(comprobante.TipoCambio) || 1,
    totalTrasladados: numero(attrImpuestos.TotalImpuestosTrasladados),
    totalRetenidos: numero(attrImpuestos.TotalImpuestosRetenidos),
    traslados: conTasa(traslados),
    retenciones: conTasa(retenciones),
    locales: leerImpuestosLocales(nodoComplemento),
    conceptos,
    complementos: {
      combustible: !!hijo(nodoComplemento, "ecc12:EstadoDeCuentaCombustible"),
      despensa: !!hijo(nodoComplemento, "valesdedespensa:ValesDeDespensa"),
      divisas: !!hijo(nodoComplemento, "divisas:Divisas"),
    },
    avisos: avisos || [],
  };
}

//  Lo capturado en el sistema

/**
 * Arma el lado "sistema" del diagnostico a partir de lo que el bloque de totales de cada
 * pantalla ya tiene en la mano, para no repetir la logica seis veces.
 *
 * Reproduce la escalera de `tasaCuota` de `calcularTotalesInsumo` (1 = % sobre la base,
 * 2 = % sobre base + trasladados previos, 3 = cuota por unidad, 4 = el importe crudo del XML,
 * 5 = exento; las retenciones solo implementan 1 y 2) para poder etiquetar cada importe con su
 * codigo del SAT y su tasa, que es lo unico comparable contra el CFDI.
 *
 * @param {Function} resolverInsumo recibe el renglon y devuelve el insumo con sus catalogos ya
 *   resueltos. En las pantallas sin OC viene en `row.insumoCompleto`; en las de orden de compra
 *   hay que buscarlo en `todasInsumos`.
 * @param {Function} [resolverRenglon] devuelve `{ cantidad, precioUnitario, descuento, subtotal }`.
 *   Hace falta en las pantallas de orden de compra: ahi la cantidad esta en `recibir` y el
 *   subtotal no vive en el renglon, se calcula al pintar los totales.
 * @param {Boolean} [omitirImpuestos] proveedor extranjero: no se le aplica ningun impuesto ni
 *   retencion, y comparar contra el catalogo del insumo daria hallazgos falsos.
 */
export function construirCapturadoDesdeTotales({
  arrayInsumos,
  resolverInsumo,
  resolverRenglon,
  omitirImpuestos,
  proveedor,
  subtotal,
  ajusteSubtotal,
  total,
  cuentasProveedor,
  arrayImpuestos,
  arrayRetenciones,
  ajusteMax,
}) {
  const renglones = [];

  (Array.isArray(arrayInsumos) ? arrayInsumos : []).forEach((valueRenglon, indice) => {
    if (!valueRenglon) return;

    const insumo = resolverInsumo ? resolverInsumo(valueRenglon) : valueRenglon.insumoCompleto;
    const datos = resolverRenglon ? resolverRenglon(valueRenglon) : valueRenglon;
    const cantidad = numero(datos.cantidad);
    const subtotalRenglon = numero(datos.subtotal);

    let acumuladoImpuestos = 0;
    const impuestos = (omitirImpuestos ? [] : insumo?.impuestos || []).map((valueImpuesto) => {
      let importe = 0;
      if (valueImpuesto.tasaCuota === 1) {
        importe = round6Decimals(subtotalRenglon * (valueImpuesto.monto / 100));
      } else if (valueImpuesto.tasaCuota === 2) {
        importe = round6Decimals(
          (subtotalRenglon + acumuladoImpuestos) * (valueImpuesto.monto / 100)
        );
      } else if (valueImpuesto.tasaCuota === 3) {
        importe = round6Decimals(cantidad * valueImpuesto.monto);
      } else if (valueImpuesto.tasaCuota === 4) {
        importe = round6Decimals(numero(valueRenglon.impuestos));
      }
      acumuladoImpuestos += importe;
      return {
        descripcionFactura: valueImpuesto.descripcionFactura,
        codigoSat: valueImpuesto.impuesto,
        tasaCuota: valueImpuesto.tasaCuota,
        monto: numero(valueImpuesto.monto),
        // tasaCuota 4 toma el importe del XML tal cual: nunca hay que reclamarle la tasa.
        comodin: valueImpuesto.tasaCuota === 4,
        tipoFactor:
          valueImpuesto.tasaCuota === 5
            ? "Exento"
            : valueImpuesto.tasaCuota === 3
            ? "Cuota"
            : "Tasa",
        importe,
      };
    });

    let acumuladoRetenciones = 0;
    const retenciones = [];
    const procesarRetencion = (valueRetencion, condicion = true) => {
      if (!condicion) return;
      let importe = 0;
      if (valueRetencion.tasaCuota === 1) {
        importe = round6Decimals(subtotalRenglon * (valueRetencion.monto / 100));
      } else if (valueRetencion.tasaCuota === 2) {
        importe = round6Decimals(
          (subtotalRenglon + acumuladoRetenciones) * (valueRetencion.monto / 100)
        );
      }
      acumuladoRetenciones += importe;
      retenciones.push({
        descripcionFactura: valueRetencion.descripcionFactura,
        codigoSat: valueRetencion.impuesto,
        tasaCuota: valueRetencion.tasaCuota,
        monto: numero(valueRetencion.monto),
        tipoFactor: "Tasa",
        importe,
      });
    };

    if (proveedor && !omitirImpuestos) {
      for (const valueRetencion of proveedor.retenciones || []) procesarRetencion(valueRetencion);
      for (const valueRetencion of insumo?.retenciones || []) {
        const aplica =
          (valueRetencion.aplicaCompra === 1 && !proveedor.persona) ||
          (valueRetencion.aplicaCompra === 2 && proveedor.persona) ||
          valueRetencion.aplicaCompra === 3;
        procesarRetencion(valueRetencion, aplica);
      }
    }

    renglones.push({
      indice,
      insumoId: valueRenglon.insumoId,
      descripcion:
        insumo?.stringBuscar || insumo?.descripcion || valueRenglon.descripcion || "Renglón " + (indice + 1),
      cantidad,
      precioUnitario: numero(datos.precioUnitario),
      descuento: numero(datos.descuento),
      subtotal: subtotalRenglon,
      impuestos,
      retenciones,
      // Los renglones que nacieron del XML conservan la identidad del concepto que los creo
      // (insumos/procesarXml devuelve `{ ...xmlData, insumoId, insumoCompleto }`).
      claveXml: {
        codigo: valueRenglon.codigo,
        descripcion: valueRenglon.descripcion,
        claveProdServ: valueRenglon.claveProdServ,
        claveUnidad: valueRenglon.claveUnidad,
      },
    });
  });

  return {
    subtotal: numero(subtotal),
    ajusteSubtotal: numero(ajusteSubtotal),
    total: numero(total),
    cuentasProveedor: numero(cuentasProveedor),
    arrayImpuestos: arrayImpuestos || [],
    arrayRetenciones: arrayRetenciones || [],
    renglones,
    ajusteMax: numero(ajusteMax),
  };
}

//  Emparejamiento renglon <-> concepto

const mismaClave = (renglon, concepto) => {
  const clave = renglon.claveXml || {};
  const igual = (a, b) => texto(a) === texto(b);
  return (
    igual(clave.codigo, concepto.codigo) &&
    igual(clave.descripcion, concepto.descripcion) &&
    igual(clave.claveProdServ, concepto.claveProdServ) &&
    igual(clave.claveUnidad, concepto.claveUnidad)
  );
};

/**
 * Empareja cada renglon capturado con el concepto del CFDI del que salio.
 *
 * Se empareja por identidad y no por posicion: `insumos/procesarXml` arma su respuesta con
 * `push` dentro de un `Promise.all(map(async ...))`, asi que el orden de regreso no es el de los
 * conceptos. Cada concepto se consume una sola vez, porque un CFDI puede repetir conceptos
 * identicos. Cuando el renglon no trae la identidad del XML (pantallas de orden de compra, donde
 * los renglones vienen de la OC) se cae al `insumoId`.
 */
const emparejarRenglones = (renglones, conceptos) => {
  const usados = new Set();
  const pares = [];

  const tomar = (predicado) => {
    for (let index = 0; index < conceptos.length; index++) {
      if (usados.has(index)) continue;
      if (predicado(conceptos[index])) {
        usados.add(index);
        return conceptos[index];
      }
    }
    return null;
  };

  for (const renglon of renglones) {
    let concepto = tomar((valueConcepto) => mismaClave(renglon, valueConcepto));

    if (!concepto && renglon.insumoId) {
      concepto = tomar(
        (valueConcepto) =>
          valueConcepto.insumoId && String(valueConcepto.insumoId) === String(renglon.insumoId)
      );
    }

    pares.push({ renglon, concepto });
  }

  const conceptosSobrantes = conceptos.filter((valueConcepto, index) => !usados.has(index));

  return { pares, conceptosSobrantes };
};

//  Agrupacion fiscal para comparar los dos lados

const agrupar = (lista) => {
  const mapa = new Map();
  for (const value of lista) {
    const tasa = value.tasaPorcentaje !== undefined ? value.tasaPorcentaje : value.monto;
    const clave = claveFiscal(value.codigoSat, value.tipoFactor, tasa);
    const previo = mapa.get(clave);
    const importe = value.importe !== undefined ? value.importe : value.total;
    if (previo) {
      previo.importe = round6Decimals(previo.importe + numero(importe));
    } else {
      mapa.set(clave, {
        clave,
        codigo: codigoSatANumero(value.codigoSat),
        tipoFactor: value.tipoFactor || "Tasa",
        tasa,
        // Sin codigo del SAT no se puede armar la etiqueta canonica; se cae al nombre del
        // catalogo, que es lo que el usuario ve en la tabla de totales.
        etiqueta: Number.isFinite(codigoSatANumero(value.codigoSat))
          ? etiquetaFiscal(value.codigoSat, value.tipoFactor, tasa)
          : value.etiquetaCruda || "sin identificar",
        comodin: !!value.comodin,
        importe: numero(importe),
      });
    }
  }
  return mapa;
};

/**
 * El lado del sistema, con los importes que de verdad se estan sumando al total.
 *
 * Los renglones sirven para saber QUE es cada importe (codigo del SAT y tasa), pero el importe
 * bueno es el de `arrayImpuestos`/`arrayRetenciones` del bloque de totales: ese ya trae el ajuste
 * manual de cada linea. Si se comparara contra el recalculado, un ajuste que el usuario acaba de
 * capturar apareceria como diferencia y ademas dejaria un residuo fantasma en "sin explicar".
 * El puente entre los dos es `descripcionFactura`, que es la misma llave que usan las pantallas.
 */
const agruparSistema = (entradasDeRenglones, arrayMostrado, campoImporte) => {
  const metadatos = new Map();
  for (const value of entradasDeRenglones) {
    if (!metadatos.has(value.descripcionFactura)) metadatos.set(value.descripcionFactura, value);
  }

  return agrupar(
    (arrayMostrado || []).map((valueMostrado) => {
      const meta = metadatos.get(valueMostrado.descripcionFactura);
      return {
        codigoSat: meta ? meta.codigoSat : NaN,
        tipoFactor: meta ? meta.tipoFactor : "Tasa",
        monto: meta ? meta.monto : 0,
        comodin: meta ? meta.comodin : false,
        etiquetaCruda: valueMostrado.descripcionFactura,
        importe: numero(valueMostrado[campoImporte]),
      };
    })
  );
};

/**
 * Compara los dos lados de un mismo tipo (traslados o retenciones) y devuelve hallazgos.
 * Primero por clave exacta (codigo + tipo + tasa); lo que sobre se vuelve a intentar por codigo,
 * que es como se detecta "la misma contribucion pero con otra tasa".
 */
const compararGrupos = (mapaXml, mapaSistema, tipo) => {
  const hallazgos = [];
  const soloXml = new Map(mapaXml);
  const soloSistema = new Map(mapaSistema);

  // `monto` siempre es cuanto aporta el hallazgo a `diferencia` (= total del XML - capturado).
  // Un impuesto que falta sube el total del XML; una retencion que falta lo BAJA, porque se resta
  // del total a pagar. Sin este signo el residuo sale al doble y todo cae en "sin explicar".
  const signo = tipo === "retencion" ? -1 : 1;

  for (const clave of mapaXml.keys()) {
    if (soloSistema.has(clave)) {
      const enXml = soloXml.get(clave);
      const enSistema = soloSistema.get(clave);
      const diferencia = round2Decimals(enXml.importe - enSistema.importe);
      if (Math.abs(diferencia) > TOPE_REDONDEO) {
        hallazgos.push({
          tipo: "importe-distinto",
          severidad: "alta",
          titulo: `${enXml.etiqueta}: el importe no coincide`,
          detalle:
            `El XML trae ${moneda(enXml.importe)} y el sistema calcula ${moneda(enSistema.importe)}. ` +
            `La tasa es la misma, así que la base sobre la que se calcula es distinta: revisa el precio, la cantidad o el descuento de los renglones.`,
          monto: signo * diferencia,
        });
      } else if (Math.abs(diferencia) >= TOLERANCIA_CENTAVO) {
        // Centavos con la tasa correcta: es el redondeo del emisor, no un error de captura.
        hallazgos.push({
          tipo: "redondeo",
          severidad: "info",
          titulo: `${enXml.etiqueta}: diferencia de redondeo`,
          detalle:
            `El XML trae ${moneda(enXml.importe)} y el sistema calcula ${moneda(enSistema.importe)}. La tasa es la ` +
            `misma: el emisor redondea el impuesto de cada concepto por separado y el sistema lo calcula sobre el ` +
            `total de cada renglón, así que se separan por centavos.`,
          monto: signo * diferencia,
        });
      }
      soloXml.delete(clave);
      soloSistema.delete(clave);
    }
  }

  // Misma contribucion, distinta tasa
  for (const enXml of Array.from(soloXml.values())) {
    const enSistema = Array.from(soloSistema.values()).find(
      (value) => value.codigo === enXml.codigo && !value.comodin
    );
    if (!enSistema) continue;
    hallazgos.push({
      tipo: "tasa-distinta",
      severidad: "alta",
      titulo: `${nombreCodigoSat(enXml.codigo)}: la tasa no coincide`,
      detalle:
        `El XML trae ${enXml.etiqueta} por ${moneda(enXml.importe)} y el sistema aplica ` +
        `${enSistema.etiqueta} por ${moneda(enSistema.importe)}. Los insumos de esta factura no tienen ` +
        `configurada la misma tasa que el CFDI.`,
      monto: signo * round2Decimals(enXml.importe - enSistema.importe),
    });
    soloXml.delete(enXml.clave);
    soloSistema.delete(enSistema.clave);
  }

  const nombreTipo = tipo === "retencion" ? "retención" : "impuesto";

  for (const enXml of soloXml.values()) {
    hallazgos.push({
      tipo: `falta-${tipo}`,
      severidad: "alta",
      titulo: `Falta ${nombreTipo === "retención" ? "la retención" : "el impuesto"} ${enXml.etiqueta}`,
      detalle:
        `El XML ${tipo === "retencion" ? "retiene" : "traslada"} ${moneda(enXml.importe)} de ` +
        `${enXml.etiqueta} y el sistema no lo está aplicando. ` +
        (tipo === "retencion"
          ? "Revisa las retenciones del proveedor y las del insumo (y su campo Aplica en compra: persona física o moral)."
          : "Revisa los impuestos configurados en los insumos de esta factura."),
      monto: signo * enXml.importe,
    });
  }

  for (const enSistema of soloSistema.values()) {
    if (enSistema.comodin) continue;
    hallazgos.push({
      tipo: `sobra-${tipo}`,
      severidad: "alta",
      titulo: `El XML no trae ${nombreTipo === "retención" ? "la retención" : "el impuesto"} ${enSistema.etiqueta}`,
      detalle:
        `El sistema está aplicando ${moneda(enSistema.importe)} de ${enSistema.etiqueta} que el CFDI no ` +
        `${tipo === "retencion" ? "retiene" : "traslada"}. Sobra en el catálogo del insumo o del proveedor.`,
      monto: -signo * enSistema.importe,
    });
  }

  return hallazgos;
};

/**
 * Cuanto se separan las dos formas de redondear el mismo impuesto: la del emisor (redondea el
 * importe de cada concepto) contra la del sistema (calcula sobre el total del renglon). Es el
 * origen clasico de los descuadres de uno o dos centavos.
 */
const deltaPorRedondeoDeConceptos = (xml) => {
  let porConcepto = 0;
  const basesPorClave = new Map();

  for (const concepto of xml.conceptos) {
    for (const traslado of concepto.traslados) {
      porConcepto += round2Decimals(traslado.importe);
      const clave = claveFiscal(traslado.codigoSat, traslado.tipoFactor, traslado.tasaPorcentaje);
      const previo = basesPorClave.get(clave) || { base: 0, tasa: traslado.tasaPorcentaje };
      previo.base += traslado.base || round6Decimals(concepto.importeNeto);
      basesPorClave.set(clave, previo);
    }
  }

  let global = 0;
  for (const value of basesPorClave.values()) {
    global += round2Decimals(value.base * (value.tasa / 100));
  }

  return round2Decimals(porConcepto - global);
};

//  El diagnostico

/**
 * @param {Object} xml salida de `leerCfdiParaDiagnostico`
 * @param {Object} capturado salida de `construirCapturadoDesdeTotales`
 * @returns {Object} { cuadra, diferencia, resumen, hallazgos, ajusteCubre }
 */
export function diagnosticarCuadreFactura({ xml, capturado }) {
  const totalCapturado = round2Decimals(capturado.total - capturado.cuentasProveedor);
  const diferencia = round2Decimals(round2Decimals(xml.total) - totalCapturado);
  const cuadra = diferencia === 0;

  const impuestosCapturados = round2Decimals(
    capturado.arrayImpuestos.reduce((acumulado, value) => acumulado + numero(value.totalImpuestos), 0)
  );
  const retencionesCapturadas = round2Decimals(capturado.cuentasProveedor);
  const subtotalCapturado = round2Decimals(capturado.subtotal);

  // El total del CFDI ya trae los impuestos locales dentro, aunque el sistema no los lea.
  const trasladadoXml = round2Decimals(xml.totalTrasladados + xml.locales.totalTraslados);
  const retenidoXml = round2Decimals(xml.totalRetenidos + xml.locales.totalRetenciones);

  const resumen = [
    {
      concepto: "Subtotal",
      xml: round2Decimals(xml.subTotalNeto),
      sistema: subtotalCapturado,
      diferencia: round2Decimals(xml.subTotalNeto - subtotalCapturado),
    },
    {
      concepto: "Impuestos trasladados",
      xml: trasladadoXml,
      sistema: impuestosCapturados,
      diferencia: round2Decimals(trasladadoXml - impuestosCapturados),
    },
    {
      concepto: "Retenciones",
      xml: retenidoXml,
      sistema: retencionesCapturadas,
      diferencia: round2Decimals(retenidoXml - retencionesCapturadas),
    },
    {
      concepto: "Total a pagar",
      xml: round2Decimals(xml.total),
      sistema: totalCapturado,
      diferencia,
    },
  ];

  const hallazgos = [];

  // 1. Impuestos locales: el sistema no los lee en ningun flujo de compras, asi que si el CFDI
  //    los trae son diferencia pura. Va primero porque es invisible en toda la pantalla.
  const totalLocales = round2Decimals(
    xml.locales.totalTraslados - xml.locales.totalRetenciones
  );
  if (xml.locales.totalTraslados !== 0 || xml.locales.totalRetenciones !== 0) {
    const nombres = [...xml.locales.traslados, ...xml.locales.retenciones]
      .map((value) => value.nombre)
      .filter(Boolean);
    hallazgos.push({
      tipo: "impuestos-locales",
      severidad: "alta",
      titulo: "El CFDI trae impuestos locales",
      detalle:
        `El comprobante incluye impuestos locales${nombres.length ? " (" + [...new Set(nombres)].join(", ") + ")" : ""} ` +
        `por ${moneda(totalLocales)}. El sistema no lee el complemento implocal en compras, así que ese importe ` +
        `no aparece en ningún renglón y sale completo como diferencia.`,
      monto: totalLocales,
    });
  }

  // 2. y 3. Retenciones e impuestos trasladados: falta / sobra / tasa distinta.
  const impuestosSistema = agruparSistema(
    capturado.renglones.flatMap((valueRenglon) => valueRenglon.impuestos),
    capturado.arrayImpuestos,
    "totalImpuestos"
  );
  const retencionesSistema = agruparSistema(
    capturado.renglones.flatMap((valueRenglon) => valueRenglon.retenciones),
    capturado.arrayRetenciones,
    "totalRetenciones"
  );

  hallazgos.push(
    ...compararGrupos(agrupar(xml.retenciones), retencionesSistema, "retencion")
  );
  hallazgos.push(...compararGrupos(agrupar(xml.traslados), impuestosSistema, "impuesto"));

  // 4. Renglon por renglon.
  const { pares, conceptosSobrantes } = emparejarRenglones(capturado.renglones, xml.conceptos);

  const renglonesImporte = [];
  const renglonesFiscal = [];
  const renglonesSinConcepto = [];

  for (const { renglon, concepto } of pares) {
    if (!concepto) {
      renglonesSinConcepto.push({
        renglon: renglon.indice + 1,
        insumo: renglon.descripcion,
        sistema: renglon.subtotal,
        xml: null,
        diferencia: -renglon.subtotal,
        nota: "No viene del XML",
      });
      continue;
    }

    const diferenciaImporte = round2Decimals(concepto.importeNeto - renglon.subtotal);
    if (Math.abs(diferenciaImporte) >= TOLERANCIA_CENTAVO) {
      const causas = [];
      if (round6Decimals(concepto.cantidad) !== round6Decimals(renglon.cantidad)) {
        causas.push(`cantidad ${renglon.cantidad} vs ${concepto.cantidad} del XML`);
      }
      if (round2Decimals(concepto.valorUnitario) !== round2Decimals(renglon.precioUnitario)) {
        causas.push(
          `precio unitario ${moneda(renglon.precioUnitario)} vs ${moneda(concepto.valorUnitario)} del XML`
        );
      }
      if (concepto.descuento !== 0 || renglon.descuento !== 0) {
        causas.push(`descuento ${renglon.descuento}% vs ${moneda(concepto.descuento)} del XML`);
      }
      renglonesImporte.push({
        renglon: renglon.indice + 1,
        insumo: renglon.descripcion,
        sistema: renglon.subtotal,
        xml: concepto.importeNeto,
        diferencia: diferenciaImporte,
        nota: causas.join(" · ") || "Importe distinto",
      });
    }

    const firmaXml = Array.from(agrupar(concepto.traslados).values())
      .map((value) => value.etiqueta)
      .sort();
    const firmaSistema = Array.from(
      agrupar(renglon.impuestos.filter((value) => !value.comodin)).values()
    )
      .map((value) => value.etiqueta)
      .sort();

    if (firmaXml.join(" + ") !== firmaSistema.join(" + ")) {
      renglonesFiscal.push({
        renglon: renglon.indice + 1,
        insumo: renglon.descripcion,
        sistema: firmaSistema.length ? firmaSistema.join(" + ") : "sin impuestos",
        xml: firmaXml.length ? firmaXml.join(" + ") : "sin impuestos",
        diferencia: round2Decimals(
          concepto.traslados.reduce((acumulado, value) => acumulado + value.importe, 0) -
            renglon.impuestos.reduce((acumulado, value) => acumulado + value.importe, 0)
        ),
      });
    }
  }

  if (renglonesFiscal.length) {
    hallazgos.push({
      tipo: "renglones-impuestos",
      severidad: "alta",
      titulo: `${renglonesFiscal.length} renglón(es) con impuestos distintos a los del XML`,
      detalle:
        "El insumo tiene configurados otros impuestos que los que el CFDI trae en ese concepto. " +
        "Corrige el catálogo del insumo (Editar Insumo · Impuestos) y vuelve a cargar el XML.",
      renglones: renglonesFiscal,
      columnas: "texto",
    });
  }

  // Cuanto aporta cada grupo de renglones a la diferencia. Los de "impuestos distintos" NO
  // aportan: ese importe ya lo contaron los hallazgos de tasa/impuesto faltante a nivel
  // comprobante, y sumarlo dos veces dejaria un residuo fantasma.
  const sumarDiferencias = (lista) =>
    round2Decimals(lista.reduce((acumulado, value) => acumulado + numero(value.diferencia), 0));

  if (renglonesImporte.length) {
    hallazgos.push({
      tipo: "renglones-importe",
      monto: sumarDiferencias(renglonesImporte),
      severidad: "media",
      titulo: `${renglonesImporte.length} renglón(es) con importe distinto al del XML`,
      detalle:
        "La cantidad, el precio unitario o el descuento capturados no son los del concepto del CFDI. " +
        "En una factura contra orden de compra esto es normal cuando el precio se tomó de la OC.",
      renglones: renglonesImporte,
    });
  }

  if (renglonesSinConcepto.length) {
    hallazgos.push({
      tipo: "renglones-sin-concepto",
      monto: sumarDiferencias(renglonesSinConcepto),
      severidad: "media",
      titulo: `${renglonesSinConcepto.length} renglón(es) no vienen del XML`,
      detalle: "Se capturaron a mano o el XML se cargó después. Suman al total pero no están en el CFDI.",
      renglones: renglonesSinConcepto,
    });
  }

  if (conceptosSobrantes.length) {
    const renglonesFaltantes = conceptosSobrantes.map((valueConcepto) => ({
      renglon: valueConcepto.indice + 1,
      insumo: valueConcepto.descripcion,
      sistema: null,
      xml: valueConcepto.importeNeto,
      diferencia: valueConcepto.importeNeto,
    }));
    hallazgos.push({
      tipo: "conceptos-sin-renglon",
      monto: sumarDiferencias(renglonesFaltantes),
      severidad: "alta",
      titulo: `${conceptosSobrantes.length} concepto(s) del XML no se capturaron`,
      detalle: "Están en el CFDI pero no tienen renglón en la factura.",
      renglones: renglonesFaltantes,
    });
  }

  // 5. Redondeo: lo que queda cuando nada de lo anterior lo explica.
  const explicado = round2Decimals(
    hallazgos.reduce((acumulado, value) => acumulado + numero(value.monto), 0)
  );
  const residuo = round2Decimals(diferencia - explicado);

  if (!cuadra && Math.abs(residuo) >= TOLERANCIA_CENTAVO) {
    const deltaRedondeo = deltaPorRedondeoDeConceptos(xml);
    const cabeEnRedondeo =
      Math.abs(residuo) <= TOPE_REDONDEO &&
      Math.abs(residuo) <= round2Decimals(0.01 * Math.max(xml.conceptos.length, 1)) + TOLERANCIA_CENTAVO;

    if (round2Decimals(deltaRedondeo) === residuo || cabeEnRedondeo) {
      hallazgos.push({
        tipo: "redondeo",
        severidad: "info",
        titulo: "Es una diferencia de redondeo",
        detalle:
          `Quedan ${moneda(residuo)} sin explicar. El emisor redondea el impuesto de cada concepto por separado y ` +
          `el sistema lo calcula sobre el total de cada renglón, así que se separan por centavos. ` +
          (capturado.ajusteMax > 0
            ? `Se puede cubrir con el ajuste (límite configurado: ±${moneda(capturado.ajusteMax)}).`
            : "Esta empresa no tiene configurado un límite de ajuste."),
        monto: residuo,
      });
    } else {
      hallazgos.push({
        tipo: "sin-explicar",
        severidad: "media",
        titulo: "Queda una diferencia sin explicar",
        detalle:
          `Después de revisar subtotal, impuestos, retenciones y renglones quedan ${moneda(residuo)} que no se ` +
          `pueden atribuir a ninguna causa conocida. Revisa el desglose contra el PDF del proveedor.`,
        monto: residuo,
      });
    }
  }

  // 6. Informativos que cambian el cuadre sin ser un error.
  if (xml.complementos.combustible || xml.complementos.despensa || xml.complementos.divisas) {
    const cuales = [
      xml.complementos.combustible && "estado de cuenta de combustible",
      xml.complementos.despensa && "vales de despensa",
      xml.complementos.divisas && "divisas",
    ].filter(Boolean);
    hallazgos.push({
      tipo: "complemento",
      severidad: "info",
      titulo: "El CFDI trae un complemento",
      detalle:
        `Incluye ${cuales.join(", ")}. Los totales del comprobante ya vienen sumados con el complemento y ` +
        `la validación de cuadre al guardar se relaja para estos casos.`,
    });
  }

  if (xml.moneda && xml.moneda.toUpperCase() !== "MXN") {
    hallazgos.push({
      tipo: "moneda",
      severidad: "info",
      titulo: `El CFDI está en ${xml.moneda}`,
      detalle: `Tipo de cambio del comprobante: ${xml.tipoCambio}. Verifica que la moneda y el tipo de cambio capturados sean los mismos.`,
    });
  }

  for (const aviso of xml.avisos) {
    hallazgos.push({ tipo: "aviso-xml", severidad: "info", titulo: "Aviso del XML", detalle: aviso });
  }

  const orden = { alta: 0, media: 1, info: 2 };
  hallazgos.sort((a, b) => orden[a.severidad] - orden[b.severidad]);

  for (const hallazgo of hallazgos) {
    if (hallazgo.monto !== undefined) hallazgo.monto = round2Decimals(hallazgo.monto);
  }

  return {
    cuadra,
    diferencia,
    totalXml: round2Decimals(xml.total),
    totalCapturado,
    resumen,
    hallazgos,
    ajusteCubre: !cuadra && Math.abs(diferencia) <= numero(capturado.ajusteMax),
    ajusteMax: numero(capturado.ajusteMax),
  };
}
